import { gbp, shortDate, localizeText } from "@/lib/format";
import { TriangleAlert, Receipt, Globe, BellRing } from "lucide-react";

export default function AlertCard({ alert, onRead, compact = false }) {
  const isOverall = alert.scope === "overall";
  const isWarning = alert.level === "warning";
  const tone = isWarning ? "amber" : "rose";
  const toneText = isWarning ? "text-amber-400" : "text-rose-400";
  const unreadBorder = isWarning ? "border-amber-500/30 bg-amber-500/10" : "border-rose-500/30 bg-rose-500/10";

  return (
    <div
      data-testid={`alert-${alert.id}`}
      onClick={() => !alert.read && onRead?.(alert.id)}
      className={`rounded-xl border p-3.5 transition ${!alert.read ? `cursor-pointer ${unreadBorder}` : "border-white/8 bg-[#0D0D0D]"}`}
    >
      <div className="flex items-start gap-2.5">
        <span className={`mt-0.5 shrink-0 ${!alert.read ? toneText : "text-slate-500"}`}>
          {isWarning ? <BellRing className="h-4 w-4" /> : isOverall ? <Globe className="h-4 w-4" /> : <TriangleAlert className="h-4 w-4" />}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${isOverall ? "border-sky-500/30 bg-sky-500/10 text-sky-300" : "border-slate-500/30 bg-slate-500/10 text-slate-300"}`}>
              {isOverall ? "Overall" : alert.category}
            </span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${isWarning ? "border-amber-500/30 bg-amber-500/10 text-amber-300" : "border-rose-500/30 bg-rose-500/10 text-rose-300"}`}>
              {isWarning ? `${alert.percent ?? 80}% warning` : "Over budget"}
            </span>
            <span className="text-[11px] text-slate-500">{shortDate(alert.created_at)}</span>
          </div>
          <p className="text-sm text-slate-200 mt-1.5">{localizeText(alert.message)}</p>

          <div className="mt-2 grid grid-cols-2 gap-2">
            <div className="rounded-lg bg-black/30 border border-white/6 px-2.5 py-1.5">
              <p className="text-[10px] text-slate-500 flex items-center gap-1"><Receipt className="h-3 w-3" /> Latest charge</p>
              <p className="text-xs font-num text-rose-300 mt-0.5">-{gbp(alert.transaction?.amount ?? alert.amount)} · {alert.transaction?.merchant ?? alert.merchant}</p>
            </div>
            {isWarning ? (
              <div className="rounded-lg bg-black/30 border border-white/6 px-2.5 py-1.5">
                <p className="text-[10px] text-slate-500">Left in budget</p>
                <p className="text-xs font-num text-[#4DE3BF] mt-0.5 font-semibold">{gbp(alert.remaining ?? 0)}</p>
              </div>
            ) : (
              <div className="rounded-lg bg-black/30 border border-white/6 px-2.5 py-1.5">
                <p className="text-[10px] text-slate-500">Over budget by</p>
                <p className="text-xs font-num text-amber-300 mt-0.5 font-semibold">{gbp(alert.over_by ?? Math.max(0, (alert.month_spend || 0) - (alert.effective_limit || alert.threshold || 0)))}</p>
              </div>
            )}
          </div>

          {!compact && alert.summary?.length > 0 && (
            <div className="mt-2 rounded-lg bg-black/30 border border-white/6 px-2.5 py-2">
              <p className="text-[10px] text-slate-500 mb-1">Spending so far this month</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                {alert.summary.slice(0, 6).map((r) => (
                  <div key={r.category} className="flex justify-between text-[11px]">
                    <span className="text-slate-400 truncate">{r.category}</span>
                    <span className="font-num text-slate-300">{gbp(r.amount)}</span>
                  </div>
                ))}
              </div>
              {alert.month_total != null && (
                <div className="flex justify-between text-[11px] mt-1 pt-1 border-t border-white/8">
                  <span className="text-[#4DE3BF] font-medium">Total</span>
                  <span className="font-num text-[#4DE3BF] font-semibold">{gbp(alert.month_total)}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
